<script setup lang="ts">
import { Head, Link } from '@inertiajs/vue3';
import { useI18n } from 'vue-i18n';
import { usePublicPageBreadcrumb } from '@/composables/usePublicPageBreadcrumb';

const { t, tm, rt } = useI18n();
usePublicPageBreadcrumb('privacy', 'pages.privacy.title');
</script>

<template>
    <Head :title="t('pages.privacy.title')">
        <meta name="description" :content="t('pages.privacy.metaDescription')" />
    </Head>

    <div class="mx-auto max-w-3xl px-4 py-16">
        <h1 class="text-3xl font-semibold tracking-tight">{{ t('pages.privacy.title') }}</h1>
        <p class="mt-2 text-sm text-muted-foreground">{{ t('pages.privacy.lastUpdated') }}</p>

        <div class="mt-10 space-y-8 text-sm leading-relaxed text-muted-foreground">
            <section>
                <h2 class="text-lg font-medium text-foreground">{{ t('pages.privacy.sections.controller.title') }}</h2>
                <p>{{ t('pages.privacy.sections.controller.body') }}</p>
            </section>

            <section>
                <h2 class="text-lg font-medium text-foreground">{{ t('pages.privacy.sections.dataWeProcess.title') }}</h2>
                <p class="font-medium text-foreground">{{ t('pages.privacy.sections.dataWeProcess.accountData.heading') }}</p>
                <ul class="mt-2 list-disc pl-5 space-y-1">
                    <li v-for="(item, i) in (tm('pages.privacy.sections.dataWeProcess.accountData.items') as string[])" :key="i">{{ rt(item) }}</li>
                </ul>
                <p class="mt-4 font-medium text-foreground">{{ t('pages.privacy.sections.dataWeProcess.creatorData.heading') }}</p>
                <ul class="mt-2 list-disc pl-5 space-y-1">
                    <li v-for="(item, i) in (tm('pages.privacy.sections.dataWeProcess.creatorData.items') as string[])" :key="i">{{ rt(item) }}</li>
                </ul>
                <p class="mt-4 font-medium text-foreground">{{ t('pages.privacy.sections.dataWeProcess.paymentData.heading') }}</p>
                <ul class="mt-2 list-disc pl-5 space-y-1">
                    <li v-for="(item, i) in (tm('pages.privacy.sections.dataWeProcess.paymentData.items') as string[])" :key="i">{{ rt(item) }}</li>
                </ul>
                <p class="mt-2">{{ t('pages.privacy.sections.dataWeProcess.paymentNote') }}</p>

                <p class="mt-4 font-medium text-foreground">{{ t('pages.privacy.sections.dataWeProcess.technicalData.heading') }}</p>
                <ul class="mt-2 list-disc pl-5 space-y-1">
                    <li v-for="(item, i) in (tm('pages.privacy.sections.dataWeProcess.technicalData.items') as string[])" :key="i">{{ rt(item) }}</li>
                </ul>
            </section>

            <section>
                <h2 class="text-lg font-medium text-foreground">{{ t('pages.privacy.sections.purposes.title') }}</h2>
                <div class="mt-3 space-y-4">
                    <div>
                        <p class="font-medium text-foreground">{{ t('pages.privacy.sections.purposes.contract.heading') }}</p>
                        <p class="mt-1">{{ t('pages.privacy.sections.purposes.contract.body') }}</p>
                    </div>
                    <div>
                        <p class="font-medium text-foreground">{{ t('pages.privacy.sections.purposes.legitimateInterest.heading') }}</p>
                        <p class="mt-1">{{ t('pages.privacy.sections.purposes.legitimateInterest.body') }}</p>
                    </div>
                    <div>
                        <p class="font-medium text-foreground">{{ t('pages.privacy.sections.purposes.legalObligation.heading') }}</p>
                        <p class="mt-1">{{ t('pages.privacy.sections.purposes.legalObligation.body') }}</p>
                    </div>
                </div>
            </section>

            <section>
                <h2 class="text-lg font-medium text-foreground">{{ t('pages.privacy.sections.sharing.title') }}</h2>
                <p>{{ t('pages.privacy.sections.sharing.body') }}</p>
                <ul class="mt-2 list-disc pl-5 space-y-1">
                    <li v-for="(item, i) in (tm('pages.privacy.sections.sharing.items') as any[])" :key="i"><strong class="text-foreground">{{ rt(item.party) }}</strong> — {{ rt(item.purpose) }}</li>
                </ul>
                <p class="mt-2">{{ t('pages.privacy.sections.sharing.note') }}</p>
            </section>

            <section>
                <h2 class="text-lg font-medium text-foreground">{{ t('pages.privacy.sections.transfers.title') }}</h2>
                <p>{{ t('pages.privacy.sections.transfers.body') }}</p>
                <p class="mt-2">{{ t('pages.privacy.sections.transfers.body2') }}</p>
            </section>

            <section>
                <h2 class="text-lg font-medium text-foreground">{{ t('pages.privacy.sections.retention.title') }}</h2>
                <p>{{ t('pages.privacy.sections.retention.body') }}</p>
                <ul class="mt-2 list-disc pl-5 space-y-1">
                    <li v-for="(item, i) in (tm('pages.privacy.sections.retention.items') as any[])" :key="i"><strong class="text-foreground">{{ rt(item.type) }}:</strong> {{ rt(item.period) }}</li>
                </ul>
            </section>

            <section>
                <h2 class="text-lg font-medium text-foreground">{{ t('pages.privacy.sections.rights.title') }}</h2>
                <p>{{ t('pages.privacy.sections.rights.body') }}</p>
                <ul class="mt-2 list-disc pl-5 space-y-1">
                    <li v-for="(item, i) in (tm('pages.privacy.sections.rights.items') as any[])" :key="i"><strong class="text-foreground">{{ rt(item.right) }}:</strong> {{ rt(item.description) }}</li>
                </ul>
                <p class="mt-2">{{ t('pages.privacy.sections.rights.note') }}</p>
            </section>

            <section>
                <h2 class="text-lg font-medium text-foreground">{{ t('pages.privacy.sections.security.title') }}</h2>
                <p>{{ t('pages.privacy.sections.security.body') }}</p>
            </section>

            <section>
                <h2 class="text-lg font-medium text-foreground">{{ t('pages.privacy.sections.cookies.title') }}</h2>
                <p>{{ t('pages.privacy.sections.cookies.body') }}</p>
            </section>

            <section>
                <h2 class="text-lg font-medium text-foreground">{{ t('pages.privacy.sections.complaints.title') }}</h2>
                <p>{{ t('pages.privacy.sections.complaints.body') }}</p>
            </section>

            <section>
                <h2 class="text-lg font-medium text-foreground">{{ t('pages.privacy.sections.changes.title') }}</h2>
                <p>{{ t('pages.privacy.sections.changes.body') }}</p>
            </section>
        </div>
    </div>
</template>
